import React from "react";
import {
  MessageSquare,
  FileText,
  Mic,
  Users,
  CheckCircle,
  Clock,
  ArrowRight,
  Sparkles,
  ArrowLeft,
  Brain,
  Zap,
  AlertCircle,
  Shield,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import MainHeader from "../MainHeader";
const MeetingSummaryHome = () => {
  const navigate = useNavigate();
  const navigateCall = (path) => {
    navigate(path);
  };

  const handlePreviousRecords = () => {
    navigateCall("/meeting_summary/records");
  };

  const handleStartMeeting = () => {
    navigateCall("/meeting_summary/start");
  };

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-blue-600 opacity-10"></div>

          {/* Back Button */}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 shadow-md hover:shadow-lg transition-all duration-300 hover:bg-white"
            >
              <ArrowLeft className="w-4 h-4 text-teal-600 group-hover:transform group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium text-gray-700">
                뒤로가기
              </span>
            </button>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-blue-600 blur-xl opacity-50"></div>
                  <MessageSquare className="relative w-20 h-20 text-teal-600" />
                </div>
              </div>

              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Meeting Summary AI
              </h1>

              <p className="text-xl text-gray-600 mb-2">
                AI 회의록 자동 요약 시스템
              </p>

              <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                회의 내용을 실시간으로 분석하여 핵심 내용 요약과 업무 분담을
                자동화합니다
              </p>
            </div>
          </div>
        </div>

        {/* Main Buttons Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Previous Records Button */}
            <button
              onClick={handlePreviousRecords}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-teal-300"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="relative text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gray-400 to-gray-500 rounded-2xl mb-4 mx-auto">
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  이전 기록 확인하기
                </h3>
                <p className="text-gray-600">
                  지난 회의 요약본과 업무 진행 상황 확인
                </p>
              </div>
            </button>

            {/* Start Meeting Button */}
            <button
              onClick={handleStartMeeting}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-blue-300"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="relative text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-teal-500 to-blue-600 rounded-2xl mb-4 mx-auto">
                  <Mic className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  미팅 시작하기
                </h3>
                <p className="text-gray-600">
                  새로운 회의를 시작하고 AI 요약 받기
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* Service Notice */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-10">
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  서비스 특징
                </h4>
                <p className="text-sm text-gray-700">
                  Meeting Summary AI는 최신 음성인식 및 자연어 처리 기술을
                  활용하여 회의 내용을 정확하게 분석합니다.
                </p>
                <p className="text-sm text-gray-700">
                  회의 종료 후 최대 15분 안에 요약복은 확인할 수 있습니다.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-white rounded-3xl shadow-xl p-10 border border-gray-100">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              스마트한 회의 관리의 시작
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-r from-teal-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <FileText className="w-7 h-7 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  회의록 자동 요약
                </h3>
                <p className="text-gray-600">
                  중요한 내용을 AI가 자동으로 정리하여 핵심만 빠르게 파악
                </p>
              </div>

              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-r from-teal-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  업무 분담 제안
                </h3>
                <p className="text-gray-600">
                  회의 내용을 바탕으로 효율적인 업무 분담을 AI가 제안
                </p>
              </div>

              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-r from-teal-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  담당자 자동 지정
                </h3>
                <p className="text-gray-600">
                  각 업무에 가장 적합한 담당자를 자동으로 매칭
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Technology Innovation Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-blue-600 opacity-5 blur-3xl"></div>
            <div className="relative bg-gradient-to-r from-teal-600 to-blue-600 rounded-3xl p-1">
              <div className="bg-white rounded-3xl p-8 md:p-12">
                <div className="flex items-center justify-center mb-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-blue-600 blur-lg opacity-50"></div>
                  </div>
                </div>

                <h3 className="text-4xl font-bold text-center mb-6 bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                  Hybrid Audio
                </h3>

                <p className="text-center text-gray-700 mb-8 text-lg">
                  시대 흐름에 발맞춰 오프라인 회의부터 Zoom·보이스톡 등
                  <br />
                  다양한 웹 미팅까지 모든 형태의 토론을 놓치지 않고 캡처하여
                  <br />
                  <span className="font-bold text-teal-600">
                    완벽한 회의 기록과 분석을 제공
                  </span>
                </p>

                <div className="grid md:grid-cols-3 gap-6 mt-10">
                  <div className="text-center">
                    <div className="flex justify-center mb-3">
                      <Mic className="w-10 h-10 text-teal-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      하이브리드 녹음
                    </h4>
                    <p className="text-sm text-gray-600">
                      마이크와 스피커 음성 동시 캡처
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="flex justify-center mb-3">
                      <Shield className="w-10 h-10 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      보안 처리
                    </h4>
                    <p className="text-sm text-gray-600">
                      분석된 녹음본은 어디에도
                    </p>
                    <p className="text-sm text-gray-600">
                      저장되지 않고 바로 파기됩니다.
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="flex justify-center mb-3">
                      <Sparkles className="w-10 h-10 text-teal-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      스마트 요약
                    </h4>
                    <p className="text-sm text-gray-600">
                      핵심 내용과 업무 분담을 한 번에
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Notice */}
        <div className="bg-gray-100 border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <p className="text-center text-sm text-gray-600">
              <span className="font-semibold">🔒 개인정보 보호</span>
              <br />
              Meeting Summary AI는 회의 내용의 보안을 최우선으로 하며, 모든
              데이터는 암호화되어 안전하게 관리됩니다. 민감한 정보가 포함된
              회의는 별도의 보안 설정을 권장합니다.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default MeetingSummaryHome;
